import DOMPurify, { type Config, type DOMPurify as DOMPurifyInstance } from 'dompurify';

/**
 * Single sanitizer for every piece of organizer- or user-authored HTML that
 * the app renders as markup (event/course descriptions, program content,
 * lesson text blocks, certificate templates).
 *
 * Profiles:
 *  - 'rich'        – rich-text editor output (TipTap: paragraphs, headings,
 *                    lists, links, images, text-align / button inline styles)
 *                    and generated program tables. No <style>, no iframes,
 *                    no svg/math, no forms. Inline styles are kept but any
 *                    declaration that can load a resource or overlay the page
 *                    is dropped.
 *  - 'certificate' – full-document certificate templates for preview and
 *                    printing. Keeps <html>/<head>/<body>, <style> blocks and
 *                    style attributes (templates depend on them, including
 *                    background url()s), but removes scripts, event handlers,
 *                    javascript:/vbscript: URLs, forms, frames and embeds.
 *
 * Server behaviour: DOMPurify needs a DOM. Without `window` (SSR, route
 * handlers) it cannot sanitize, so this module NEVER returns markup there –
 * it returns the plain text of the input, HTML-escaped. Rich rendering only
 * happens in the browser. React components should use <SafeHtml>, which
 * handles hydration on top of this.
 */

export type SanitizeProfile = 'rich' | 'certificate';

const RICH_ALLOWED_TAGS = [
  'p', 'br', 'hr', 'div', 'span',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'mark',
  'sub', 'sup', 'small', 'code', 'pre', 'blockquote',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
];

const RICH_ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title',
  'src', 'alt', 'width', 'height',
  'class', 'style', 'align',
  'colspan', 'rowspan', 'start',
];

// Absolute http(s)/mailto/tel, or relative URLs (no scheme).
const SAFE_URI = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

const RICH_CONFIG: Config = {
  ALLOWED_TAGS: RICH_ALLOWED_TAGS,
  ALLOWED_ATTR: RICH_ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_URI_REGEXP: SAFE_URI,
};

const CERTIFICATE_CONFIG: Config = {
  WHOLE_DOCUMENT: true,
  // DOMPurify's default HTML + SVG allowlist (includes <style> and the style
  // attribute) minus everything that can submit, frame, embed or redirect.
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
  ADD_TAGS: ['style'],
  FORBID_TAGS: [
    'script', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
    'form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'datalist',
    'base', 'link', 'meta', 'math', 'portal', 'template', 'use', 'foreignobject', 'animate',
    'set', 'animatemotion', 'animatetransform',
  ],
  FORBID_ATTR: ['action', 'formaction', 'xlink:href', 'srcset', 'ping'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: SAFE_URI,
};

// --- CSS filtering ---

// Anything that can execute (legacy engines) or fetch a resource, plus
// escapes/comments that could be used to hide those tokens.
const RICH_CSS_FORBIDDEN = /url\s*\(|image-set|expression|javascript:|vbscript:|-moz-binding|behavior|@import|\\|\/\*/i;
// Script-capable CSS only (legacy engines). Certificates keep url()/@import
// for backgrounds and fonts.
const CERT_CSS_FORBIDDEN = /expression\s*\(|javascript:|vbscript:|-moz-binding|behavior\s*:/i;

function cleanRichStyle(value: string): string {
  return value
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => {
      if (!decl) return false;
      const colon = decl.indexOf(':');
      if (colon <= 0) return false;
      const prop = decl.slice(0, colon).trim().toLowerCase();
      // position:fixed/absolute lets content overlay the host page.
      if (prop === 'position') return false;
      return !RICH_CSS_FORBIDDEN.test(decl);
    })
    .join('; ');
}

function cleanCertificateStyle(value: string): string {
  return value
    .split(';')
    .filter((decl) => !CERT_CSS_FORBIDDEN.test(decl))
    .join(';')
    .trim();
}

function cleanCertificateStylesheet(css: string): string {
  // Neutralise the forbidden tokens wherever they appear (also inside
  // comments/strings – harmless to mangle those).
  return css.replace(new RegExp(CERT_CSS_FORBIDDEN.source, 'gi'), '/* removed */');
}

// --- URL checks applied after DOMPurify's own attribute pass ---

const SAFE_IMG_SRC = /^(?:https:\/\/|data:image\/(?:png|jpe?g|gif|webp|avif|bmp|svg\+xml)[;,])/i;

function isSafeImgSrc(src: string): boolean {
  return SAFE_IMG_SRC.test(src.trim());
}

function createPurifier(win: Window & typeof globalThis, profile: SanitizeProfile): DOMPurifyInstance {
  const purify = DOMPurify(win);

  purify.addHook('uponSanitizeElement', (node, data) => {
    if (profile === 'certificate' && data.tagName === 'style' && node.textContent) {
      node.textContent = cleanCertificateStylesheet(node.textContent);
    }
  });

  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;
    const cleaned = profile === 'rich' ? cleanRichStyle(data.attrValue) : cleanCertificateStyle(data.attrValue);
    if (cleaned) {
      data.attrValue = cleaned;
    } else {
      data.keepAttr = false;
    }
  });

  purify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    if (!el.tagName) return;
    const tag = el.tagName.toLowerCase();

    if (tag === 'a') {
      const target = el.getAttribute('target');
      if (target !== null) {
        if (target === '_blank') {
          el.setAttribute('rel', 'noopener noreferrer');
        } else {
          el.removeAttribute('target');
        }
      }
    }

    if (tag === 'img') {
      const src = el.getAttribute('src');
      if (src !== null && !isSafeImgSrc(src)) {
        el.removeAttribute('src');
      }
    }
  });

  return purify;
}

const purifiers: Partial<Record<SanitizeProfile, DOMPurifyInstance>> = {};

function getPurifier(profile: SanitizeProfile): DOMPurifyInstance | null {
  if (typeof window === 'undefined' || typeof window.document === 'undefined') return null;
  let purify = purifiers[profile];
  if (!purify) {
    purify = createPurifier(window, profile);
    if (!purify.isSupported) return null;
    purifiers[profile] = purify;
  }
  return purify;
}

// --- Server fallback: plain text only ---

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'",
};

/**
 * Plain-text rendering of an HTML string without a DOM. Output is TEXT, not
 * markup – callers must still escape it (React does that for text children;
 * sanitizeHtml escapes it before returning).
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<(script|style|template|noscript|iframe|object|svg|math)\b[\s\S]*?<\/\1\s*>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote|pre)\s*>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/<|>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|apos|#39);/gi, (_m, name: string) => NAMED_ENTITIES[name.toLowerCase()] ?? ' ')
    .replace(/[ \t\f\r]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** True when full DOM-based sanitizing is available (i.e. in the browser). */
export function canSanitizeHtml(): boolean {
  return getPurifier('rich') !== null;
}

/**
 * Sanitize untrusted HTML for insertion via dangerouslySetInnerHTML /
 * document.write. In the browser returns sanitized markup; without a DOM
 * returns the escaped plain text (never markup).
 */
export function sanitizeHtml(html: string | null | undefined, profile: SanitizeProfile = 'rich'): string {
  if (!html) return '';
  const purify = getPurifier(profile);
  if (!purify) {
    return escapeHtml(htmlToPlainText(String(html)));
  }
  const config = profile === 'certificate' ? CERTIFICATE_CONFIG : RICH_CONFIG;
  return purify.sanitize(String(html), { ...config, RETURN_TRUSTED_TYPE: false }) as string;
}
